import { Form } from '~/shared/forms/Form';
import { useFindings } from '~/features/concepts/findings/useFindings';
import { useVisit } from '../VisitContext';
import {
	calculateOasisFieldValuesOptions,
	getDiagnosesOptions,
	getOasisAssessmentValuesForQuestionOptions,
	type OasisTaskViewModel
} from '@generated';
import { FieldActions } from '~/libraries/forms/field-actions/FieldActions';
import type { FormValues } from '~/shared/forms/types';
import { convertClientValuesToServerValues } from '~/libraries/forms/values';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Section } from '~/shared/section/Section';
import { Button } from '~/shared/components/buttons/Button';
import { Info, History, FileText } from 'lucide-react';
import { IconButtonPopover } from '~/shared/IconButtonPopover';
import { DateTimeFormField } from '~/shared/forms/controls/fields/date-fields/DateTimeFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { Tag, TagGroup } from '~/shared/components/TagGroup';
import { FormFields } from '~/libraries/forms/FormFields';
import { Copyright } from '~/shared/Copyright';
import { getOasisInitialValues } from './values';
import Yup from '~/util/yup';
import { generateValidationSchema } from '~/libraries/forms/validation';
import { holdUntilExternalReferencesAreResolved } from '../shared/util';
import { skipOasisQuestion } from './util';
import type { FormikValues } from 'formik';
import { VisitOasisTaskCollaboration } from './collaboration/VisitOasisTaskCollaboration';
import { SectionTitle } from '~/shared/section/SectionTitle';
import { calculateCollaborationDivergence, markOriginalCollaborationAnswers } from './collaboration/util';
import { Banner } from '~/shared/components/Banner';

export function VisitOasisTask({ oasisTask, siblingOasisTasks }: { oasisTask: OasisTaskViewModel; siblingOasisTasks: OasisTaskViewModel[] }) {
	const { stayId, oasisTasks, visitFindings } = useVisit();
	const { data, isLoading } = useQuery({
		...getOasisAssessmentValuesForQuestionOptions(oasisTask.workflowCorrelationId, oasisTask.worklflowSystemName),
		enabled: oasisTask.isCollaboration
	});

	const { data: patientDiagnoses } = useSuspenseQuery(getDiagnosesOptions(stayId));
	const { findings } = useFindings(stayId);

	if (isLoading) {
		return <div>Loading...</div>;
	}
	const diagnoses = patientDiagnoses.pending
		? patientDiagnoses.pending.diagnoses
		: patientDiagnoses.current
		? patientDiagnoses.current.diagnoses
		: [];
	const initialOasisValues = getOasisInitialValues(oasisTask, oasisTasks, findings, visitFindings, diagnoses);

	const initialValues = {
		...initialOasisValues,
		collaborationAnswerDiverged: oasisTask.isCollaboration
			? calculateCollaborationDivergence(oasisTask, data ? data.values : {}, initialOasisValues)
			: false,
		divergenceReason: oasisTask.divergenceReason ?? null
	};

	const validationSchema = Yup.object().shape({
		...generateValidationSchema(oasisTask.oasisDefinition.documentationFormFields),
		collaborationAnswerDiverged: Yup.boolean().required(),
		divergenceReason: Yup.string()
			.label('Addendum Reason')
			.when('collaborationAnswerDiverged', {
				is: true,
				then: schema => schema.required('Please provide a reason for the divergence.'),
				otherwise: schema => schema.nullable()
			})
	});

	const transformValuesForCalculation = (fieldName: string, values: FormValues) => {
		return {
			oasisDefinitionId: oasisTask.oasisDefinitionId,
			fieldName,
			values: convertClientValuesToServerValues(values, oasisTask.oasisDefinition.documentationFormFields),
			collaborationAnswerDiverged: values.collaborationAnswerDiverged,
			divergenceReason: values.collaborationAnswerDiverged ? values.divergenceReason : null
		};
	};

	const copyrightExists = oasisTask.oasisDefinition.metadata['copyright'] !== undefined;
	const siblingTaskIds = siblingOasisTasks.map(x => x.id);
	const holdForExternalReferences = holdUntilExternalReferencesAreResolved(
		{
			...oasisTask,
			externalReferences: oasisTask.oasisDefinition.externalReferences ?? [],
			systemName: oasisTask.oasisDefinition.systemName,
			category: oasisTask.oasisDefinition.category
		},
		siblingOasisTasks.map(x => ({
			...x,
			externalReferences: x.oasisDefinition.externalReferences ?? [],
			systemName: x.oasisDefinition.systemName,
			category: x.oasisDefinition.category
		})),
		oasisTasks
			.filter(x => x.id !== oasisTask.id && !siblingTaskIds.includes(x.id))
			.map(x => ({
				...x,
				externalReferences: x.oasisDefinition.externalReferences ?? [],
				systemName: x.oasisDefinition.systemName,
				category: x.oasisDefinition.category
			}))
	);

	return (
		<Form
			initialValues={initialValues}
			validationSchema={validationSchema}
			registryId={oasisTask.id}
			fieldCalculation={{
				calculateMutation: calculateOasisFieldValuesOptions,
				transformValuesForCalculation
			}}
		>
			{formHelpers => {
				const skipDocumentation = skipOasisQuestion(oasisTask, formHelpers.values);
				return (
					<>
						<div className={skipDocumentation ? 'hidden' : ''}>
							<Section
								className="border-gray-200 bg-white"
								header={
									<SectionTitle
										title={oasisTask.oasisDefinition.title}
										actions={
											<div className="flex items-center gap-2 text-gray-400">
												<IconButtonPopover
													Icon={Info}
													aria-label="Show Info"
													popoverText={
														<>
															<div className="prose max-w-sm">OASIS Details: Todo</div>
															{oasisTask.valuesLastUpdatedOn && (
																<div className="flex items-center gap-2 p-4 pl-0 text-base text-gray-500">
																	<History className="size-4" />
																	<div className="flex flex-col">
																		<div>Last updated on:</div>
																		<div>
																			{oasisTask.valuesLastUpdatedOn &&
																				oasisTask.valuesLastUpdatedOn.toLocaleString()}
																		</div>
																	</div>
																</div>
															)}
														</>
													}
												/>
												<IconButtonPopover
													Icon={FileText}
													aria-label="Edit Note"
													popoverText={
														<>
															<DateTimeFormField name="performedOn" granularity="minute" />
															<TextAreaFormField name="notes" label="Notes" />
														</>
													}
												/>
												<Button Icon={History} aria-label="View History" onClick={() => console.log('show history')} />
												{oasisTask.oasisDefinition.flags && oasisTask.oasisDefinition.flags.length > 0 && (
													<TagGroup selectionMode="none" aria-label="Documentation Flags">
														{oasisTask.oasisDefinition.flags.map(x => (
															<Tag key={x}>{x}</Tag>
														))}
													</TagGroup>
												)}
											</div>
										}
									/>
								}
							>
								<div className="grid grid-cols-1 gap-8">
									<OasisBucketItemTaskBody
										holdForExternalReferences={holdForExternalReferences}
										oasisTask={oasisTask}
										currentOasisValues={data ? data.values : undefined}
									/>
									{copyrightExists && <Copyright copyright={oasisTask.oasisDefinition.metadata['copyright']} />}
									{formHelpers.values.collaborationAnswerDiverged && (
										<Banner
											intent="warning"
											title="Addendum Diverged"
											message="The answers in this collaboration diverge from the original answers. Please provide a reason for the divergence."
										>
											<TextAreaFormField name="divergenceReason" hideLabel />
										</Banner>
									)}
								</div>
								{oasisTask.isCollaboration && (
									<VisitOasisTaskCollaboration oasisTask={oasisTask} originalValues={data ? data.values : {}} />
								)}
							</Section>
						</div>
						<FieldActions formFields={oasisTask.oasisDefinition.documentationFormFields} />
					</>
				);
			}}
		</Form>
	);
}

function OasisBucketItemTaskBody({
	holdForExternalReferences,
	oasisTask,
	currentOasisValues
}: {
	holdForExternalReferences: boolean;
	oasisTask: OasisTaskViewModel;
	currentOasisValues?: FormikValues;
}) {
	if (holdForExternalReferences) {
		return (
			<p className="mb-4 rounded border-l-4 border-blue-500 bg-blue-50 p-4 pl-6 text-blue-700">
				This documentation references fields from other documentations: {oasisTask.oasisDefinition.externalReferences.join(', ')}.
			</p>
		);
	}

	return oasisTask.oasisDefinition.documentationFormControls?.length > 0 ? (
		<FormFields
			formControls={oasisTask.oasisDefinition.documentationFormControls}
			formFields={oasisTask.oasisDefinition.documentationFormFields}
			additionalProcessing={currentOasisValues ? formControls => markOriginalCollaborationAnswers(formControls, currentOasisValues) : undefined}
		/>
	) : null;
}
